import { useMemo } from "react";
import { useLingui } from "@lingui/react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { axisWidthFor, formatAxisValue, type ChartUnit } from "@/components/cockpit/chartFormat";
import { formatMonthYear } from "@/lib/format";
import { formatMetricValue } from "@/lib/metrics/metricFormat";
import type { MetricDefinition, MetricPoint, MetricUnit } from "@/lib/metrics/types";

const PART_COLOURS = [1, 2, 3, 4, 5, 6].map((n) => `hsl(var(--chart-${n}))`);

/** The axis unit of a metric unit: whole figures keep the axis narrow. */
function axisUnit(unit: MetricUnit): ChartUnit {
  if (unit === "chf") return "currency";
  if (unit === "percent") return "percent";
  if (unit === "minutes") return "minutes";
  return "count";
}

/** The parts a stacked chart draws, in the registry's order, then any unknown key. */
export function chartParts(def: MetricDefinition, points: readonly MetricPoint[]): string[] {
  if (!def.stacked) return [];
  const seen = new Set<string>();
  for (const p of points) for (const k of Object.keys(p.breakdown ?? {})) seen.add(k);
  const ordered = Object.keys(def.breakdownLabels ?? {}).filter((k) => seen.has(k));
  return [...ordered, ...[...seen].filter((k) => !ordered.includes(k))];
}

interface MetricChartProps {
  def: MetricDefinition;
  points: MetricPoint[];
  currency: string;
  height?: number;
}

/**
 * The months of a metric against the same months a year earlier (#2175 B3):
 * bars or a line for the period's year, a dashed line for the previous year,
 * and the parts stacked where the registry declares them.
 */
export function MetricChart({ def, points, currency, height = 280 }: MetricChartProps) {
  const { i18n } = useLingui();
  const parts = useMemo(() => chartParts(def, points), [def, points]);
  const data = useMemo(
    () =>
      points.map((p) => ({
        period: p.period,
        value: p.value,
        previous: p.previous,
        ...Object.fromEntries(parts.map((k) => [`part_${k}`, p.breakdown?.[k] ?? 0])),
      })),
    [points, parts],
  );
  const hasPrevious = points.some((p) => p.previous !== null);
  const label = i18n._(def.label);
  const currentName = def.forward
    ? label
    : i18n._({ id: "analytics.metrics.chart.current", message: "This period" });
  const previousName = i18n._({ id: "analytics.metrics.previousYear", message: "Previous year" });
  const partName = (k: string) => (def.breakdownLabels?.[k] ? i18n._(def.breakdownLabels[k]) : k);
  const fmt = (v: unknown) =>
    typeof v === "number" ? formatMetricValue(def.unit, v, i18n, currency) : "";
  const axis = (v: number) => formatAxisValue(v, axisUnit(def.unit), i18n.locale, currency);
  const axisWidth = useMemo(() => {
    const values = data.flatMap((d) => [d.value, d.previous]).filter((v): v is number => typeof v === "number");
    const extremes = values.length > 0 ? [Math.max(...values), Math.min(...values, 0)] : [0];
    // The ticks sit inside the width after recharts' tick margin: leave room so a
    // money label ("CHF 10'000") stays on one line.
    return axisWidthFor(extremes.map((v) => formatAxisValue(v, axisUnit(def.unit), i18n.locale, currency))) + 16;
  }, [data, def.unit, i18n.locale, currency]);

  return (
    // `cockpit-chart` carries the shared axis and legend type sizes.
    <div data-testid="metric-chart" className="cockpit-chart">
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={data}
          margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
          aria-label={i18n._({ id: "cockpit.chart.ariaLabel", message: "{metric} chart", values: { metric: label } })}
        >
          <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="period"
            tickFormatter={(p: string) => formatMonthYear(p, i18n.locale, "medium") || p}
            tickLine={false}
            axisLine={false}
            minTickGap={12}
          />
          <YAxis tickFormatter={axis} tickLine={false} axisLine={false} width={axisWidth} />
          <Tooltip
            formatter={(v: unknown) => fmt(v)}
            labelFormatter={(p: unknown) => (typeof p === "string" ? formatMonthYear(p, i18n.locale, "long") || p : "")}
          />
          <Legend />
          {parts.length > 0
            ? parts.map((k, i) => (
                <Bar
                  key={k}
                  dataKey={`part_${k}`}
                  name={partName(k)}
                  stackId="parts"
                  fill={PART_COLOURS[i % PART_COLOURS.length]}
                  maxBarSize={28}
                />
              ))
            : def.chart === "bar"
              ? <Bar dataKey="value" name={currentName} fill={PART_COLOURS[0]} radius={[3, 3, 0, 0]} maxBarSize={28} />
              : <Line dataKey="value" name={currentName} stroke={PART_COLOURS[0]} strokeWidth={2} dot={false} connectNulls />}
          {hasPrevious ? (
            <Line
              dataKey="previous"
              name={previousName}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="4 4"
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
